<template>
  <div class="paco-status">
    <div class="paco-status__card">

      <!-- State 1: Polling -->
      <div v-if="status === 'polling'" class="paco-status__state">
        <div class="paco-status__spinner">
          <svg viewBox="0 0 50 50" class="paco-status__spinner-svg">
            <circle cx="25" cy="25" r="20" fill="none" stroke-width="4" />
          </svg>
        </div>
        <h2 class="paco-status__title">กำลังตรวจสอบการชำระเงิน</h2>
        <div class="paco-status__progress">
          <div class="paco-status__progress-bar"></div>
        </div>
      </div>

      <!-- State 2: Failed (payment definitively failed) -->
      <div v-if="status === 'failed'" class="paco-status__state">
        <div class="paco-status__icon" :class="isCancelled ? 'paco-status__icon--warning' : 'paco-status__icon--error'">
          <svg v-if="!isCancelled" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <svg v-if="isCancelled" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <h2 class="paco-status__title" :class="isCancelled ? 'paco-status__title--warning' : 'paco-status__title--error'">{{ isCancelled ? 'การชำระเงินถูกยกเลิก' : 'การชำระเงินไม่สำเร็จ' }}</h2>
        <p class="paco-status__subtitle">{{ failureMessage }}</p>
        <p v-if="failureInfo && failureInfo.respCode" class="paco-status__ref">รหัสอ้างอิง: {{ failureInfo.respCode }}<br v-if="failureInfo.respDesc">{{ failureInfo.respDesc }}</p>
        <button class="paco-status__btn" @click="goToCheckout">ลองชำระเงินอีกครั้ง</button>
      </div>

      <!-- State 3: Timeout (unconfirmed) -->
      <div v-if="status === 'timeout'" class="paco-status__state">
        <div class="paco-status__icon paco-status__icon--warning">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 class="paco-status__title paco-status__title--warning">ไม่สามารถยืนยันการชำระเงินได้</h2>
        <p class="paco-status__subtitle">กรุณาตรวจสอบอีเมลของคุณ หรือติดต่อเราเพื่อยืนยันสถานะการบริจาค</p>
        <button class="paco-status__btn" @click="goToHome">กลับไปยังหน้าหลัก</button>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PacoStatus',
  data () {
    return {
      status: 'polling',
      orderId: '',
      polling: null,
      failureInfo: null
    }
  },
  mounted () {
    this.orderId = this.$route.query.orderId
    this.preview = this.$route.query.preview === '1'
    if (this.preview) {
      this.status = this.$route.query.state || 'polling'
      return
    }
    if (!this.orderId) {
      this.status = 'timeout'
      return
    }
    this.startPolling()
  },
  beforeDestroy () {
    if (this.polling) {
      clearInterval(this.polling)
    }
  },
  computed: {
    isCancelled () {
      if (!this.failureInfo) return false
      const code = this.failureInfo.respCode || ''
      const cancelCodes = ['PC-B050910', 'PC-B052323', 'PC-B052386', 'PC-B052425']
      return this.failureInfo.status === 'C' || cancelCodes.indexOf(code) > -1
    },
    failureMessage () {
      if (!this.failureInfo) return ''
      const code = this.failureInfo.respCode || ''

      // Cancelled by donor
      if (this.isCancelled) {
        return 'คุณได้ยกเลิกการชำระเงิน'
      }

      // Card declined / authorization
      const declinedCodes = ['PC-B050031', 'PC-B051120', 'PC-B052311']
      if (declinedCodes.indexOf(code) > -1) {
        return 'บัตรของคุณถูกปฏิเสธ กรุณาตรวจสอบกับธนาคารผู้ออกบัตร'
      }

      // Insufficient funds
      if (code === 'PC-B052357') {
        return 'ยอดเงินในบัตรไม่เพียงพอ'
      }

      // Expired card
      if (code === 'PC-B052360') {
        return 'บัตรหมดอายุ กรุณาใช้บัตรใบอื่น'
      }

      // Incorrect PIN
      if (code === 'PC-B052361') {
        return 'รหัส PIN ไม่ถูกต้อง'
      }

      // 3DS authentication
      if (code === 'PC-B050030' || code === 'PC-B050059') {
        return 'การยืนยันตัวตนไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'
      }

      // Card not permitted
      if (code === 'PC-B052363' || code === 'PC-B052364') {
        return 'บัตรนี้ไม่สามารถทำรายการได้ กรุณาใช้บัตรใบอื่น'
      }

      // Expired / timeout at PACO
      const expiredCodes = ['PC-B050061', 'PC-B050077', 'PC-B052399']
      if (this.failureInfo.status === 'E' || expiredCodes.indexOf(code) > -1) {
        return 'การชำระเงินหมดเวลา กรุณาลองใหม่อีกครั้ง'
      }

      // Fraud
      if (code === 'PC-B051920' || code === 'PC-B051921') {
        return 'ไม่สามารถดำเนินการชำระเงินได้ กรุณาติดต่อธนาคารผู้ออกบัตร'
      }

      // Generic with code for support reference
      return 'ไม่สามารถดำเนินการชำระเงินได้ กรุณาลองใหม่อีกครั้ง'
    }
  },
  methods: {
    startPolling () {
      const maxAttempts = 10
      let attempts = 0

      this.polling = setInterval(async () => {
        attempts++
        if (attempts > maxAttempts) {
          clearInterval(this.polling)
          this.status = 'timeout'
          return
        }

        try {
          const resp = await this.$http.get(`paco/status?orderId=${this.orderId}`)
          if (resp.body && resp.body.paid) {
            clearInterval(this.polling)
            this.$store.dispatch('clearCart')
            const thankYouId = resp.body.publicOrderId || this.orderId
            this.$router.push('/thank-you/' + thankYouId)
            return
          }
          if (resp.body && resp.body.failed) {
            clearInterval(this.polling)
            this.failureInfo = {
              status: resp.body.status,
              respCode: resp.body.respCode,
              respDesc: resp.body.respDesc
            }
            this.status = 'failed'
            return
          }
        } catch (e) {
          // Continue polling on error
        }
      }, 3000)
    },
    goToCheckout () {
      this.$store.dispatch('resetPayment').then(() => {
        this.$router.push('/checkout')
      })
    },
    goToHome () {
      this.$store.dispatch('resetPayment').then(() => {
        this.$router.push('/')
      })
    }
  }
}
</script>

<style scoped>
.paco-status {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  background: #CCE9EE;
}

.paco-status__card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 20px rgba(21, 48, 80, 0.08);
  max-width: 480px;
  width: 100%;
  padding: 3rem 2.5rem;
  text-align: center;
}

.paco-status__state {
  animation: fadeInUp 0.4s ease-out;
}

/* Spinner */
.paco-status__spinner {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
}

.paco-status__spinner-svg {
  animation: spin 1.2s linear infinite;
  width: 100%;
  height: 100%;
}

.paco-status__spinner-svg circle {
  stroke: #38AB49;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

/* Progress bar */
.paco-status__progress {
  margin-top: 2rem;
  height: 3px;
  background: #e8e8e8;
  border-radius: 3px;
  overflow: hidden;
}

.paco-status__progress-bar {
  height: 100%;
  background: #38AB49;
  border-radius: 3px;
  animation: progress 30s linear;
  width: 0%;
}

/* Icons */
.paco-status__icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  animation: scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.paco-status__icon svg {
  width: 32px;
  height: 32px;
}

.paco-status__icon--error {
  background: #fbe9e7;
  color: #c62828;
}

.paco-status__icon--warning {
  background: #fff3e0;
  color: #e65100;
}

/* Typography */
.paco-status__title {
  font-family: 'Wittaya-Medium', Thonburi, Arial, sans-serif;
  font-size: 1.5rem;
  color: #153050;
  margin: 0 0 0.75rem;
  line-height: 1.4;
}

.paco-status__title--error {
  color: #c62828;
}

.paco-status__title--warning {
  color: #e65100;
}

.paco-status__subtitle {
  font-family: 'Wittaya-Light', Arial, sans-serif;
  font-size: 1.05rem;
  color: #546e7a;
  margin: 0 0 0.5rem;
  line-height: 1.6;
}

.paco-status__ref {
  font-family: 'Wittaya-Light', Arial, sans-serif;
  font-size: 0.85rem;
  color: #b0bec5;
  margin: 0.75rem 0 0;
}

/* Button */
.paco-status__btn {
  display: inline-block;
  margin-top: 1.5rem;
  padding: 0.75rem 2rem;
  background: #1E416D;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'Wittaya-Medium', Thonburi, Arial, sans-serif;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.2s;
  cursor: pointer;
}

.paco-status__btn:hover {
  background: #153050;
  color: #fff;
  text-decoration: none;
}

/* Animations */
@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes dash {
  0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
  100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
}

@keyframes progress {
  from { width: 0%; }
  to { width: 95%; }
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
}

@media (max-width: 480px) {
  .paco-status__card {
    padding: 2rem 1.5rem;
  }
  .paco-status__title {
    font-size: 1.25rem;
  }
}
</style>



// WEBPACK FOOTER //
// PacoStatus.vue?4b568ab3